// src/features/surveys/SurveyRunner.js
// Full-screen survey player: an intro screen, then one question per screen
// with a progress bar, Back / Next, and a thank-you screen after submitting.
// It only collects answers — saving is done by the caller via onSubmit.
// Designed to be reused for the dropout survey later.
import React, { useState, useMemo } from 'react';
import { tr, hasLanguage } from './surveyDefinitions';

const TEAL = '#19a4a0';

function SurveyRunner({ survey, learnerName, onSubmit, onClose }) {
  const hindiReady = useMemo(() => hasLanguage(survey, 'hi'), [survey]);
  const [lang, setLang] = useState('en');
  const [step, setStep] = useState(-1); // -1 = intro, 0..n-1 = questions
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Questions can be hidden depending on earlier answers (`showIf`).
  const visibleQuestions = survey.questions.filter(q => !q.showIf || q.showIf(answers));
  const total = visibleQuestions.length;
  const question = step >= 0 ? visibleQuestions[step] : null;
  const isLast = step === total - 1;
  // Button / message text in the chosen language (falls back to English).
  const ui = (key, fallback) => tr(survey.ui?.[key], lang) || fallback;

  const setAnswer = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    setError('');
  };

  const toggleMulti = (q, option) => {
    const current = Array.isArray(answers[q.id]) ? answers[q.id] : [];
    let next;
    if (current.includes(option.value)) {
      next = current.filter(v => v !== option.value);
    } else if (option.exclusive) {
      next = [option.value];
    } else {
      const exclusiveValues = q.options.filter(o => o.exclusive).map(o => o.value);
      next = [...current.filter(v => !exclusiveValues.includes(v)), option.value];
    }
    const updates = { [q.id]: next };
    // Clear the "Other" text if Other is no longer selected.
    q.options.filter(o => o.withText && !next.includes(o.value))
      .forEach(() => { updates[`${q.id}_other`] = ''; });
    setAnswers(prev => ({ ...prev, ...updates }));
    setError('');
  };

  const isAnswered = (q) => {
    const a = answers[q.id];
    if (q.type === 'multi') return Array.isArray(a) && a.length > 0;
    if (q.type === 'text') return typeof a === 'string' && a.trim() !== '';
    return a !== undefined && a !== null && a !== '';
  };

  // Only keep answers to questions that are still visible.
  const cleanAnswers = () => {
    const out = {};
    visibleQuestions.forEach(q => {
      if (!isAnswered(q)) return;
      out[q.id] = q.type === 'text' ? answers[q.id].trim() : answers[q.id];
      const otherText = answers[`${q.id}_other`];
      if (otherText && otherText.trim()) out[`${q.id}_other`] = otherText.trim();
    });
    return out;
  };

  const handleNext = async () => {
    if (question.required && !isAnswered(question)) {
      setError(ui('required', 'Please choose an answer to continue.'));
      return;
    }
    if (!isLast) {
      setStep(s => s + 1);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(cleanAnswers(), lang);
      setDone(true);
    } catch (e) {
      console.error('Error submitting survey:', e);
      setError(ui('sendError', 'Could not send your answers. Please check your internet and try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = done ? 100 : step < 0 ? 0 : Math.round((step / total) * 100);

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={tr(survey.title, lang)}>
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{tr(survey.title, lang)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {hindiReady && (
              <div style={styles.langSwitch}>
                {['en', 'hi'].map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    style={{
                      ...styles.langBtn,
                      background: lang === l ? TEAL : 'white',
                      color: lang === l ? 'white' : TEAL
                    }}
                  >
                    {l === 'en' ? 'English' : 'हिंदी'}
                  </button>
                ))}
              </div>
            )}
            <button onClick={onClose} style={styles.closeBtn} aria-label="Close">✕</button>
          </div>
        </div>

        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>

        <div style={styles.body}>
          {/* Thank-you */}
          {done && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '40px' }}>🙏</div>
              <p style={styles.questionText}>{tr(survey.thankYou, lang)}</p>
              <button onClick={onClose} style={styles.primaryBtn}>{ui('close', 'Close')}</button>
            </div>
          )}

          {/* Intro */}
          {!done && step < 0 && (
            <div style={{ padding: '1rem 0' }}>
              {learnerName && <p style={{ fontSize: '1.05rem', margin: '0 0 0.5rem' }}>{ui('greeting', 'Hi')} {learnerName},</p>}
              <p style={{ lineHeight: 1.6, color: '#374151', margin: '0 0 1.5rem' }}>{tr(survey.intro, lang)}</p>
              <button onClick={() => setStep(0)} style={styles.primaryBtn}>{tr(survey.startLabel, lang) || 'Start'}</button>
            </div>
          )}

          {/* Question */}
          {!done && question && (
            <>
              <div style={styles.counter}>{step + 1} / {total}</div>
              <p style={styles.questionText}>{tr(question.text, lang)}</p>
              {question.hint && <p style={styles.hint}>{tr(question.hint, lang)}</p>}

              {question.type === 'single' && (
                <div style={styles.optionList}>
                  {question.options.map(o => {
                    const selected = answers[question.id] === o.value;
                    return (
                      <button
                        key={o.value}
                        onClick={() => setAnswer(question.id, o.value)}
                        style={{ ...styles.option, ...(selected ? styles.optionSelected : {}) }}
                        aria-pressed={selected}
                      >
                        <span style={{ ...styles.radio, ...(selected ? styles.radioSelected : {}) }} />
                        {tr(o.label, lang)}
                      </button>
                    );
                  })}
                </div>
              )}

              {question.type === 'multi' && (
                <div style={styles.optionList}>
                  {question.options.map(o => {
                    const selected = (answers[question.id] || []).includes(o.value);
                    return (
                      <React.Fragment key={o.value}>
                        <button
                          onClick={() => toggleMulti(question, o)}
                          style={{ ...styles.option, ...(selected ? styles.optionSelected : {}) }}
                          aria-pressed={selected}
                        >
                          <span style={{ ...styles.checkbox, ...(selected ? styles.checkboxSelected : {}) }}>
                            {selected ? '✓' : ''}
                          </span>
                          {tr(o.label, lang)}
                        </button>
                        {o.withText && selected && (
                          <input
                            type="text"
                            value={answers[`${question.id}_other`] || ''}
                            onChange={e => setAnswer(`${question.id}_other`, e.target.value)}
                            placeholder={ui('otherPlaceholder', 'Please tell us more (optional)')}
                            maxLength={300}
                            style={styles.textInput}
                            autoFocus
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {question.type === 'text' && (
                <textarea
                  value={answers[question.id] || ''}
                  onChange={e => setAnswer(question.id, e.target.value)}
                  rows={4}
                  maxLength={1000}
                  style={{ ...styles.textInput, resize: 'vertical' }}
                  autoFocus
                />
              )}

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.navRow}>
                <button
                  onClick={() => { setStep(s => s - 1); setError(''); }}
                  style={styles.secondaryBtn}
                  disabled={submitting}
                >
                  {ui('back', '← Back')}
                </button>
                <button onClick={handleNext} style={styles.primaryBtn} disabled={submitting}>
                  {submitting
                    ? ui('sending', 'Sending…')
                    : isLast
                      ? ui('submit', 'Submit')
                      : question.type === 'text' && !isAnswered(question) ? ui('skip', 'Skip →') : ui('next', 'Next →')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
  },
  panel: {
    background: 'white', borderRadius: '14px', width: '100%', maxWidth: '560px',
    maxHeight: '100%', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', gap: '8px', flexWrap: 'wrap'
  },
  title: { fontWeight: 700, color: TEAL, fontSize: '1rem' },
  langSwitch: { display: 'flex', border: `1px solid ${TEAL}`, borderRadius: '8px', overflow: 'hidden' },
  langBtn: { border: 'none', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 },
  closeBtn: {
    border: 'none', background: 'transparent', fontSize: '1.1rem', cursor: 'pointer',
    color: '#6b7280', padding: '4px 6px'
  },
  progressTrack: { height: '4px', background: '#e5e7eb' },
  progressFill: { height: '100%', background: TEAL, transition: 'width 0.2s' },
  body: { padding: '18px 20px 22px' },
  counter: { fontSize: '0.8rem', color: '#6b7280', marginBottom: '6px' },
  questionText: { fontSize: '1.1rem', fontWeight: 600, color: '#111827', margin: '0 0 6px', lineHeight: 1.4 },
  hint: { fontSize: '0.85rem', color: '#6b7280', margin: '0 0 12px' },
  optionList: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' },
  option: {
    display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left',
    padding: '12px 14px', borderWidth: '1px', borderStyle: 'solid', borderColor: '#d1d5db', borderRadius: '10px',
    background: 'white', fontSize: '0.95rem', cursor: 'pointer', color: '#111827'
  },
  optionSelected: { borderColor: TEAL, background: '#f0fdfa' },
  radio: {
    width: '16px', height: '16px', borderRadius: '50%', borderWidth: '2px', borderStyle: 'solid', borderColor: '#9ca3af', flexShrink: 0
  },
  radioSelected: { borderColor: TEAL, background: TEAL, boxShadow: 'inset 0 0 0 3px white' },
  checkbox: {
    width: '18px', height: '18px', borderRadius: '4px', borderWidth: '2px', borderStyle: 'solid', borderColor: '#9ca3af', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: 'white'
  },
  checkboxSelected: { borderColor: TEAL, background: TEAL },
  textInput: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '0.95rem', fontFamily: 'inherit', marginTop: '8px'
  },
  error: { color: '#ff4747', fontSize: '0.9rem', margin: '12px 0 0' },
  navRow: { display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '20px' },
  primaryBtn: {
    background: TEAL, color: 'white', border: 'none', borderRadius: '8px',
    padding: '10px 20px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px',
    padding: '10px 16px', fontSize: '0.95rem', cursor: 'pointer'
  }
};

export default SurveyRunner;